/**
 * Hook for Stripe customer migration
 * Provides sync, import, and manual association capabilities
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stripeMigrationApi } from '../services/mandate.service';
import type {
  StripeSyncOptions,
  StripeSyncResult,
  StripeCSVImportRow,
  StripeImportResult,
  StripeAssociateRequest,
  UnassociatedStripeCustomer,
  StripeCustomerInfo,
  PaymentMandate,
} from '../types/mandate.types';

// Query keys
export const stripeMigrationKeys = {
  all: ['stripeMigration'] as const,
  unassociated: () => [...stripeMigrationKeys.all, 'unassociated'] as const,
  lookup: (id: string) => [...stripeMigrationKeys.all, 'lookup', id] as const,
};

/**
 * Hook to sync customers from Stripe
 */
export function useSyncStripeCustomers() {
  const queryClient = useQueryClient();

  return useMutation<StripeSyncResult, Error, StripeSyncOptions | undefined>({
    mutationFn: stripeMigrationApi.sync,
    onSuccess: () => {
      // Invalidate unassociated customers and mandate list
      queryClient.invalidateQueries({ queryKey: stripeMigrationKeys.unassociated() });
      queryClient.invalidateQueries({ queryKey: ['mandates'] });
    },
  });
}

/**
 * Hook to import Stripe customers from CSV/JSON
 */
export function useImportStripeCustomers() {
  const queryClient = useQueryClient();

  return useMutation<StripeImportResult, Error, StripeCSVImportRow[]>({
    mutationFn: stripeMigrationApi.import,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stripeMigrationKeys.unassociated() });
      queryClient.invalidateQueries({ queryKey: ['mandates'] });
    },
  });
}

/**
 * Hook to manually associate a Stripe customer to a client
 */
export function useAssociateStripeCustomer() {
  const queryClient = useQueryClient();

  return useMutation<PaymentMandate, Error, StripeAssociateRequest>({
    mutationFn: stripeMigrationApi.associate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stripeMigrationKeys.unassociated() });
      queryClient.invalidateQueries({ queryKey: ['mandates'] });
    },
  });
}

/**
 * Hook to get unassociated Stripe customers
 */
export function useUnassociatedStripeCustomers() {
  return useQuery<UnassociatedStripeCustomer[], Error>({
    queryKey: stripeMigrationKeys.unassociated(),
    queryFn: stripeMigrationApi.getUnassociated,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to lookup a Stripe customer by ID
 */
export function useLookupStripeCustomer(stripeCustomerId: string | null) {
  return useQuery<StripeCustomerInfo | null, Error>({
    queryKey: stripeMigrationKeys.lookup(stripeCustomerId || ''),
    queryFn: () => stripeMigrationApi.lookup(stripeCustomerId!),
    enabled: !!stripeCustomerId,
    staleTime: 60000, // 1 minute
  });
}
